import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as sprintApi from '../services/sprintService';
import type {
  CreateSprintData,
  UpdateSprintData,
  CompleteSprintData,
} from '../services/sprintService';

export const sprintKeys = {
  all: (projectId: string) => ['sprints', projectId] as const,
  backlog: (projectId: string) => ['sprints', projectId, 'backlog'] as const,
};

// --- Sprint list ---
export function useSprints(projectId: string) {
  return useQuery({
    queryKey: sprintKeys.all(projectId),
    queryFn: () => sprintApi.fetchSprints(projectId),
    enabled: !!projectId,
  });
}

// --- Backlog issues ---
export function useBacklogIssues(
  projectId: string,
  params?: { issueType?: string[]; priority?: string[]; assigneeId?: string },
) {
  return useQuery({
    queryKey: [...sprintKeys.backlog(projectId), params],
    queryFn: () => sprintApi.fetchBacklogIssues(projectId, params),
    enabled: !!projectId,
  });
}

// --- Create sprint ---
export function useCreateSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSprintData | void) =>
      sprintApi.createSprint(projectId, data || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintKeys.all(projectId) });
    },
  });
}

// --- Update sprint ---
export function useUpdateSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; data: UpdateSprintData }) =>
      sprintApi.updateSprint(projectId, params.sprintId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintKeys.all(projectId) });
    },
  });
}

// --- Delete sprint ---
export function useDeleteSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sprintId: string) =>
      sprintApi.deleteSprint(projectId, sprintId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintKeys.all(projectId) });
    },
  });
}

// --- Start sprint ---
export function useStartSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sprintId: string) =>
      sprintApi.startSprint(projectId, sprintId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintKeys.all(projectId) });
    },
  });
}

// --- Complete sprint ---
export function useCompleteSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; data?: CompleteSprintData }) =>
      sprintApi.completeSprint(projectId, params.sprintId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintKeys.all(projectId) });
      queryClient.invalidateQueries({ queryKey: sprintKeys.backlog(projectId) });
    },
  });
}

// --- Add item to sprint ---
export function useAddItemToSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; issueId: string }) =>
      sprintApi.addItemToSprint(projectId, params.sprintId, params.issueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintKeys.all(projectId) });
      queryClient.invalidateQueries({ queryKey: sprintKeys.backlog(projectId) });
    },
  });
}

// --- Bulk add items ---
export function useBulkAddItems(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; issueIds: string[] }) =>
      sprintApi.addBulkItemsToSprint(projectId, params.sprintId, params.issueIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintKeys.all(projectId) });
      queryClient.invalidateQueries({ queryKey: sprintKeys.backlog(projectId) });
    },
  });
}

// --- Remove item from sprint ---
export function useRemoveItemFromSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; issueId: string }) =>
      sprintApi.removeItemFromSprint(projectId, params.sprintId, params.issueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintKeys.all(projectId) });
      queryClient.invalidateQueries({ queryKey: sprintKeys.backlog(projectId) });
    },
  });
}
